import { useState, useEffect, useCallback, useRef } from "react";
import api from "../utils/api/axiosInstance";
import { syncActiveFeedMatchToLocalCache } from "../utils/homeChatMatchPersistence";
import { syncWizardFromFeedMatch } from "../utils/resolveMatchIntent";

/**
 * Load / save the user's persisted home feed match (MatchAnalytics feed_chat).
 */
export const useActiveFeedMatch = (userId, enabled = true) => {
  const [activeMatch, setActiveMatch] = useState(null);
  const [loading, setLoading] = useState(false);
  // Distinct from "no saved match" so the UI can offer a retry instead of
  // silently rendering an empty state when the request itself failed.
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);
  const lastSessionIdRef = useRef(null);

  const loadActiveFeedMatch = useCallback(
    async (sessionId = null) => {
      if (!userId) {
        setActiveMatch(null);
        setError(null);
        return null;
      }

      const requestId = ++requestIdRef.current;
      lastSessionIdRef.current = sessionId;

      try {
        setLoading(true);
        setError(null);
        const res = await api.get("/analytics/active-feed-match", {
          params: sessionId ? { sessionId } : {},
        });
        if (requestId !== requestIdRef.current) return null;

        const match = res.data?.activeMatch || null;
        syncWizardFromFeedMatch(match);
        setActiveMatch(match);
        syncActiveFeedMatchToLocalCache(match, userId);
        return match;
      } catch (err) {
        if (requestId !== requestIdRef.current) return null;
        console.error("Failed to load active feed match:", err);
        setActiveMatch(null);
        setError(err?.response?.data?.message || "Failed to load your saved match");
        return null;
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [userId],
  );

  const retryLoadActiveFeedMatch = useCallback(
    () => loadActiveFeedMatch(lastSessionIdRef.current),
    [loadActiveFeedMatch],
  );

  const saveActiveFeedMatch = useCallback(
    async ({ products, searchParams, matchProfile, wizardAnswers }) => {
      if (!userId || !products?.length) return null;

      try {
        const res = await api.post("/analytics/active-feed-match", {
          products,
          searchParams,
          matchProfile,
          wizardAnswers,
        });
        const match = res.data?.activeMatch || null;
        syncWizardFromFeedMatch(match);
        setActiveMatch(match);
        syncActiveFeedMatchToLocalCache(match, userId);
        return match;
      } catch (err) {
        console.error("Failed to save active feed match:", err);
        return null;
      }
    },
    [userId],
  );

  useEffect(() => {
    if (!enabled || !userId) {
      setActiveMatch(null);
      return;
    }
    loadActiveFeedMatch();
  }, [enabled, userId, loadActiveFeedMatch]);

  const activeDecision = activeMatch?.activeDecision || null;

  return {
    activeMatch,
    activeDecision,
    decisionStage: activeDecision?.stage || activeMatch?.decisionStage || null,
    loading,
    error,
    loadActiveFeedMatch,
    retryLoadActiveFeedMatch,
    saveActiveFeedMatch,
  };
};

export default useActiveFeedMatch;
